import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Lightbulb, PenTool, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RecipeInspiration } from '@/components/ai/recipe-inspiration'
import { RecipeTextParser } from '@/components/ai/recipe-text-parser'
import { RecipeForm } from '@/components/recipe/recipe-form'
import { aiRecipeParser } from '@/services/ai-recipe-parser'
import { useSettingsStore } from '@/stores/settings-store'

export default function NewRecipePage() {
  const [mode, setMode] = useState<'choose' | 'ai' | 'inspiration' | 'manual'>('choose')
  const [platformAIConfigured, setPlatformAIConfigured] = useState<boolean | null>(aiRecipeParser.peekStatus())
  const { aiEnabled } = useSettingsStore()

  useEffect(() => {
    void aiRecipeParser.getStatus().then(setPlatformAIConfigured)
  }, [])

  const isAIAvailable = aiEnabled && platformAIConfigured !== false

  if (mode === 'ai') {
    return (
      <div className="container py-6 sm:py-8">
        <div className="mb-6">
          <Button variant="outline" onClick={() => setMode('choose')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar opties
          </Button>
        </div>

        <RecipeTextParser />
      </div>
    )
  }

  if (mode === 'manual') {
    return (
      <div className="container py-6 sm:py-8">
        <div className="mb-6">
          <Button variant="outline" onClick={() => setMode('choose')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar opties
          </Button>
        </div>

        <RecipeForm mode="create" />
      </div>
    )
  }

  if (mode === 'inspiration') {
    return (
      <div className="container py-6 sm:py-8">
        <div className="mb-6">
          <Button variant="outline" onClick={() => setMode('choose')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar opties
          </Button>
        </div>

        <RecipeInspiration />
      </div>
    )
  }

  return (
    <div className="container py-6 sm:py-8">
      <div className="mb-6">
        <Button variant="outline" asChild>
          <Link to="/recepten">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar recepten
          </Link>
        </Button>
      </div>

      <div className="mb-8">
        <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">Nieuw recept toevoegen</h1>
        <p className="text-gray-600">Kies de manier die voor jou het makkelijkst is.</p>
      </div>

      <div className="grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-3">
        {aiEnabled ? (
          <Card className="cursor-pointer border-2 transition-shadow hover:border-orange-200 hover:shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center">
                <Wand2 className="mr-3 h-6 w-6 text-orange-600" />
                Recept overnemen met hulp
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-gray-600">
                Plak een recepttekst of gebruik een receptlink. De app vult daarna automatisch titel, ingrediënten en stappen voor je in.
              </p>
              <div className="mb-6 space-y-2 text-sm text-gray-500">
                <div className="flex items-center">
                  <span className="mr-2 h-2 w-2 rounded-full bg-green-500"></span>
                  Herkent ingrediënten automatisch
                </div>
                <div className="flex items-center">
                  <span className="mr-2 h-2 w-2 rounded-full bg-green-500"></span>
                  Zet losse tekst om naar duidelijke kookstappen
                </div>
                <div className="flex items-center">
                  <span className="mr-2 h-2 w-2 rounded-full bg-green-500"></span>
                  Probeert ook tijd, porties en moeilijkheid in te vullen
                </div>
              </div>
              <Button onClick={() => setMode('ai')} className="w-full" disabled={!isAIAvailable}>
                <Wand2 className="mr-2 h-4 w-4" />
                {isAIAvailable ? 'Recept laten invullen' : 'Tijdelijk niet beschikbaar'}
              </Button>
              {!isAIAvailable ? (
                <p className="mt-2 text-center text-xs text-gray-500">
                  Deze hulp werkt pas nadat de beheerder de slimme functies heeft ingeschakeld.
                </p>
              ) : platformAIConfigured === null ? (
                <p className="mt-2 text-center text-xs text-gray-500">
                  We controleren op de achtergrond of deze hulp klaarstaat.
                </p>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-dashed opacity-50">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center">
                <Wand2 className="mr-3 h-6 w-6 text-gray-400" />
                Recept overnemen met hulp
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-gray-500">
                Deze hulp is nu niet beschikbaar. De beheerder kan dit inschakelen in de instellingen.
              </p>
              <Button variant="outline" asChild className="w-full">
                <Link to="/instellingen">Naar instellingen</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {aiEnabled ? (
          <Card className="cursor-pointer border-2 transition-shadow hover:border-orange-200 hover:shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center">
                <Lightbulb className="mr-3 h-6 w-6 text-orange-600" />
                Inspiratie opdoen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-gray-600">
                Beschrijf waar je zin in hebt. De app bedenkt 5 verschillende receptideeën waaruit je kunt kiezen.
              </p>
              <div className="mb-6 space-y-2 text-sm text-gray-500">
                <div className="flex items-center">
                  <span className="mr-2 h-2 w-2 rounded-full bg-orange-500"></span>
                  Geeft 5 verschillende ideeën
                </div>
                <div className="flex items-center">
                  <span className="mr-2 h-2 w-2 rounded-full bg-orange-500"></span>
                  Jij kiest welke ideeën je wilt bewaren
                </div>
                <div className="flex items-center">
                  <span className="mr-2 h-2 w-2 rounded-full bg-orange-500"></span>
                  Werkt gekozen ideeën uit tot echte recepten
                </div>
              </div>
              <Button onClick={() => setMode('inspiration')} className="w-full" disabled={!isAIAvailable}>
                <Lightbulb className="mr-2 h-4 w-4" />
                {isAIAvailable ? 'Inspiratie starten' : 'Tijdelijk niet beschikbaar'}
              </Button>
            </CardContent>
          </Card>
        ) : null}

        <Card className="cursor-pointer border-2 transition-shadow hover:border-orange-200 hover:shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center">
              <PenTool className="mr-3 h-6 w-6 text-orange-600" />
              Zelf invullen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-gray-600">
              Vul het recept zelf stap voor stap in. Handig als je alles precies wilt nalopen of aanpassen.
            </p>
            <div className="mb-6 space-y-2 text-sm text-gray-500">
              <div className="flex items-center">
                <span className="mr-2 h-2 w-2 rounded-full bg-blue-500"></span>
                Jij bepaalt elk veld zelf
              </div>
              <div className="flex items-center">
                <span className="mr-2 h-2 w-2 rounded-full bg-blue-500"></span>
                Rustig formulier per onderdeel
              </div>
              <div className="flex items-center">
                <span className="mr-2 h-2 w-2 rounded-full bg-blue-500"></span>
                Werkt zonder slimme hulp
              </div>
            </div>
            <Button onClick={() => setMode('manual')} variant="outline" className="w-full">
              <PenTool className="mr-2 h-4 w-4" />
              Zelf invullen
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-8 max-w-4xl">
        <CardHeader>
          <CardTitle className="text-lg">Handige tips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 text-sm md:grid-cols-2">
            <div>
              <h4 className="mb-2 font-medium">Als je hulp gebruikt:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• Kopieer zoveel mogelijk van het volledige recept, inclusief ingrediënten</li>
                <li>• Hoe duidelijker tijden en porties zijn vermeld, hoe beter de app kan helpen</li>
                <li>• Controleer het resultaat altijd even voordat je opslaat</li>
                <li>• Je kunt daarna in gewone taal vragen om nog iets te veranderen</li>
              </ul>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Als je zelf invult:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• Gebruik duidelijke hoeveelheden bij ingrediënten</li>
                <li>• Houd kookstappen kort en praktisch</li>
                <li>• Voeg tags toe zodat je recepten later sneller terugvindt</li>
                <li>• Noteer ook de keukenapparatuur als die belangrijk is</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
